import { fetch_personas_by_domain, fetch_generated_personas, fetch_saved_personas_by_domain, fetch_persona_domain_schema } from '@/api.js';
import { formatAttributeDisplayValue } from '@/utils/attributeValueCoercion';
import { formatDate } from '@/utils/dateFormat';

const RESERVED_DATA_KEYS = new Set([
  'personal_characteristics',
  'personalCharacteristics',
  'personal_interests',
  'personalInterests',
  'Personal Interests',
  'image',
  'description',
  'first_name',
  'last_name',
  'big_five_profile',
  'bigFiveProfile',
  'personality',
  'devices',
  // Seed-catalog bookkeeping (data.id), not a persona attribute.
  'id',
]);

const STATIC_IMPORTS = {
  // Image/slug catalog only — lists come from GET /personas/{domain}.
  // Renamed from personas*.js so a missing/renamed file cannot break list screens
  // (loadStaticPersonasForLookup catches and returns []).
  telco: () => import(/* webpackChunkName: "personas-telco-static" */ '@/personas_telco_static'),
  automotive: () => import(/* webpackChunkName: "personas-automotive-static" */ '@/personas_automotive_static'),
  top_management: () => import(/* webpackChunkName: "personas-top-management-static" */ '@/personas_top_management_static'),
  energy: () => import(/* webpackChunkName: "personas-energy-static" */ '@/personas_energy_static'),
};

/**
 * Optional static catalog lookup for **images / slugs only**.
 * List screens must use loadDomainPersonasFromApi (GET /personas/{domain}).
 * personas_*_tbd.json twins are unused archives — do not import them.
 * Dynamic import: if a *_static.js file is missing, returns [] and lists still work.
 */
export async function loadStaticPersonasForLookup(domain) {
  const loader = STATIC_IMPORTS[domain];
  if (!loader) return [];
  try {
    const mod = await loader();
    const list = mod.personas || mod.personasTopManagement || mod.default || [];
    return Array.isArray(list) ? list : [];
  } catch (error) {
    console.warn(`Static persona catalog unavailable for ${domain}:`, error?.message || error);
    return [];
  }
}

export async function loadDomainSchemaAttributes(domain) {
  if (!domain) return [];
  try {
    const response = await fetch_persona_domain_schema(domain);
    return response?.data?.attributes || [];
  } catch (error) {
    console.warn(`Persona schema unavailable for ${domain}:`, error?.message || error);
    return [];
  }
}

export function getSavedPersonaDataMap(persona) {
  if (!persona) return {};

  if (persona.data) {
    if (Array.isArray(persona.data)) {
      return Object.fromEntries(
        persona.data
          .filter((entry) => entry?.key)
          .map((entry) => [entry.key, entry.value]),
      );
    }
    if (typeof persona.data === 'object') {
      return { ...persona.data };
    }
  }

  // Static / grid persona cards often have no nested `data` — synthesize a
  // map from top-level fields and personal_characteristics so schema keys
  // like annual_income resolve via their income alias on every page.
  const pc = persona.personal_characteristics || persona.personalCharacteristics || {};
  return {
    ...pc,
    personal_characteristics: pc,
    personalCharacteristics: pc,
  };
}

export function getSavedPersonaDisplayName(persona, unnamedLabel = 'Saved Persona') {
  if (persona?.name) return persona.name;
  if (persona?.title) return persona.title;
  const data = getSavedPersonaDataMap(persona);
  const pc = data.personal_characteristics || data.personalCharacteristics || {};
  const first = data.first_name || data.name || pc.first_name || '';
  const last = data.last_name || data.surname || pc.last_name || '';
  const full = `${first} ${last}`.trim();
  return full || unnamedLabel;
}

const SAVED_PERSONA_FIELD_ALIASES = {
  occupation: ['occupation', 'job_title'],
  job_title: ['job_title', 'occupation'],
  first_name: ['first_name', 'name'],
  last_name: ['last_name', 'surname'],
  civil_status: ['civil_status', 'civilStatus', 'marital_status'],
  marital_status: ['marital_status', 'civil_status', 'civilStatus'],
  geographic_area_of_residence: ['geographic_area_of_residence', 'residence', 'area'],
  residence: ['residence', 'geographic_area_of_residence', 'area'],
  area: ['area', 'geographic_area_of_residence', 'residence'],
  qualification: ['qualification', 'education_level'],
  education_level: ['education_level', 'qualification'],
  income: ['income', 'annual_income'],
  annual_income: ['annual_income', 'income'],
  country: ['country', 'geographic_area_of_residence', 'residence', 'area'],
  segment: ['segments', 'segmentation', 'segment', 'archetype'],
  segments: ['segments', 'segmentation', 'segment'],
  segmentation: ['segmentation', 'segments', 'segment'],
  description: ['description', 'Personality'],
  interests: ['personal_interests', 'personalInterests', 'Personal Interests'],
  personal_interests: ['personal_interests', 'personalInterests', 'Personal Interests'],
  years_of_service: ['years_of_service', 'yearsOfService'],
  yearsOfService: ['yearsOfService', 'years_of_service'],
  education: ['qualification', 'education_level'],
};

const IDENTITY_ATTRIBUTE_KEYS = new Set(['first_name', 'last_name', 'name', 'surname']);

const GENERATED_GRID_EXCLUDED_KEYS = new Set([
  'first_name',
  'last_name',
  'name',
  'surname',
  'age',
  'occupation',
  'job_title',
  'description',
]);

const PERSONA_ATTRIBUTE_ICONS = {
  age: 'mdi-calendar-month',
  gender: 'mdi-gender-male-female',
  occupation: 'mdi-briefcase-variant',
  job_title: 'mdi-briefcase-variant',
  civil_status: 'mdi-heart',
  civilStatus: 'mdi-heart',
  marital_status: 'mdi-heart',
  geographic_area_of_residence: 'mdi-home-city',
  residence: 'mdi-home-city',
  area: 'mdi-home-city',
  qualification: 'mdi-school',
  education_level: 'mdi-school',
  income: 'mdi-currency-usd',
  annual_income: 'mdi-currency-usd',
  segmentation: 'mdi-account-group',
  segments: 'mdi-account-group',
  segment: 'mdi-account-group',
  country: 'mdi-earth',
  years_of_service: 'mdi-timer',
  yearsOfService: 'mdi-timer',
  number_of_children: 'mdi-account-child',
  preferred_contact_method: 'mdi-message-text',
  payment_terms: 'mdi-calendar-clock',
  current_operator: 'mdi-cellphone-wireless',
  plan_type: 'mdi-card-account-details',
  active_services: 'mdi-check-all',
  data_usage: 'mdi-database',
  average_monthly_spend: 'mdi-cash',
  churn_propensity: 'mdi-alert-circle-outline',
  personal_interests: 'mdi-heart-multiple',
  interests: 'mdi-heart-multiple',
};

function hasResolvedValue(value) {
  return value !== undefined && value !== null && value !== '';
}

export function resolveSavedPersonaFieldValue(persona, key) {
  if (!persona || !key) return undefined;

  const candidates = SAVED_PERSONA_FIELD_ALIASES[key] || [key];
  const data = getSavedPersonaDataMap(persona);
  const nestedPc = {
    ...(data.personal_characteristics || {}),
    ...(data.personalCharacteristics || {}),
    ...(persona.personal_characteristics || {}),
    ...(persona.personalCharacteristics || {}),
  };

  // Prefer top-level saved fields (Edit Persona / API) over nested pc copies
  // so annual_income edits win over a stale personal_characteristics.income.
  for (const candidate of candidates) {
    if (hasResolvedValue(data[candidate])) {
      return data[candidate];
    }
    if (hasResolvedValue(persona[candidate])) {
      return persona[candidate];
    }
    if (hasResolvedValue(nestedPc[candidate])) {
      return nestedPc[candidate];
    }
  }

  return undefined;
}

export function getSavedPersonaField(persona, key, fallback = '') {
  const value = resolveSavedPersonaFieldValue(persona, key);
  if (value === undefined || value === null || value === '') return fallback;
  if (Array.isArray(value)) return value.length ? value.join(', ') : fallback;
  return value;
}

export function getPersonaAttributeIcon(key) {
  return PERSONA_ATTRIBUTE_ICONS[key] || 'mdi-tag-outline';
}

const GENERATED_SCHEMA_FALLBACK_KEYS = [
  'gender',
  'marital_status',
  'number_of_children',
  'education_level',
  'annual_income',
  'area',
  'segments',
  'country',
  'preferred_contact_method',
];

const EMPTY_ATTRIBUTE_VALUE = '—';

function formatSavedPersonaDisplayValue(raw, { showEmpty = false } = {}) {
  if (raw === undefined || raw === null || raw === '') {
    return showEmpty ? EMPTY_ATTRIBUTE_VALUE : null;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return null;
  if (Array.isArray(raw)) {
    if (!raw.length) return showEmpty ? EMPTY_ATTRIBUTE_VALUE : null;
    return raw.join(', ');
  }
  return raw;
}

export function getSavedPersonaSchemaDisplayFields(
  persona,
  schemaAttributes,
  { excludeKeys = GENERATED_GRID_EXCLUDED_KEYS } = {},
) {
  const excluded = excludeKeys || GENERATED_GRID_EXCLUDED_KEYS;
  const ordered = [...(schemaAttributes || [])]
    .filter((attr) => attr?.key && !IDENTITY_ATTRIBUTE_KEYS.has(attr.key) && !excluded.has(attr.key))
    .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

  const attributeKeys = ordered.length
    ? ordered.map((attr) => ({ key: attr.key, label: attr.label || attr.key }))
    : GENERATED_SCHEMA_FALLBACK_KEYS
      .filter((key) => !excluded.has(key))
      .map((key) => ({ key, label: key }));

  const schemaByKey = Object.fromEntries(
    (schemaAttributes || []).filter((attr) => attr?.key).map((attr) => [attr.key, attr]),
  );

  const fields = [];
  for (const attr of attributeKeys) {
    const raw = resolveSavedPersonaFieldValue(persona, attr.key);
    if (typeof raw === 'object' && raw !== null && !Array.isArray(raw)) continue;
    const schemaAttr = schemaByKey[attr.key];
    const normalized = formatAttributeDisplayValue(raw, schemaAttr);
    const value = formatSavedPersonaDisplayValue(normalized, { showEmpty: true });
    if (value === null) continue;
    fields.push({
      key: attr.key,
      label: attr.label || attr.key,
      value,
      icon: getPersonaAttributeIcon(attr.key),
    });
  }
  return fields;
}

export function formatSavedPersonaCreatedAt(persona, locale = 'en') {
  return formatDate(persona?.created_at, locale);
}

function looksLikeImageSlug(value) {
  return typeof value === 'string' && /^[a-z0-9]+(?:_[a-z0-9]+)+$/i.test(value);
}

/** Canonical stored path (/imgs/...) for comparing image references. */
export function normalizeAssetReference(path) {
  if (!path) return '';
  let value = String(path).trim();
  if (!value) return '';

  const base = process.env.BASE_URL || '/';
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  if (value.startsWith(normalizedBase)) {
    value = value.slice(normalizedBase.length);
  }

  if (!value.startsWith('imgs/') && !value.startsWith('/imgs/')) {
    value = value.startsWith('personas_pics/')
      ? `imgs/${value}`
      : `imgs/${value.replace(/^\/+/, '')}`;
  }

  return value.startsWith('/') ? value : `/${value}`;
}

/** Public URL for a file under frontend public/ (respects vue.config publicPath). */
export function staticAssetPath(path) {
  if (!path) return '';
  const value = String(path).trim();
  if (!value) return '';
  if (/^(https?:|data:|blob:)/i.test(value)) return value;

  const normalized = normalizeAssetReference(value);
  const base = process.env.BASE_URL || '/';
  const normalizedBase = base.endsWith('/') ? base : `${base}/`;
  const relative = normalized.startsWith('/') ? normalized.slice(1) : normalized;
  return `${normalizedBase}${relative}`;
}

export function resolvePersonaImage(persona, { fallback = 'default' } = {}) {
  if (persona?.image) {
    return staticAssetPath(persona.image);
  }

  const data = getSavedPersonaDataMap(persona);
  if (data.image) {
    return staticAssetPath(data.image);
  }

  const slug = persona?.image_slug || persona?.static_name || data.image_slug
    || (looksLikeImageSlug(persona?.name) ? persona.name : null);
  if (slug) {
    const filename = String(slug).includes('.') ? slug : `${slug}.jpg`;
    return staticAssetPath(`/imgs/personas_pics/${filename}`);
  }

  if (fallback === 'empty') return '';
  return staticAssetPath('/imgs/personas_pics/female_21_student.jpg');
}

/**
 * Map a Mongo/API persona document into the UI shape used by Personas / Chat / Interview.
 */
export function mapApiPersonaToUi(doc, { t } = {}) {
  if (!doc) return null;

  const data = doc.data && typeof doc.data === 'object' && !Array.isArray(doc.data)
    ? { ...doc.data }
    : getSavedPersonaDataMap(doc);

  const pc = {
    ...(data.personal_characteristics || {}),
    ...(data.personalCharacteristics || {}),
  };

  for (const [key, value] of Object.entries(data)) {
    if (RESERVED_DATA_KEYS.has(key)) continue;
    if (value === undefined || value === null || value === '') continue;
    if (typeof value === 'object') continue;

    const aliasCandidates = SAVED_PERSONA_FIELD_ALIASES[key] || [key];
    const hasAliasValue = aliasCandidates.some(
      (aliasKey) => hasResolvedValue(pc[aliasKey]),
    );
    if (!hasAliasValue) {
      const targetKey = aliasCandidates.find((aliasKey) => aliasKey in pc) || key;
      pc[targetKey] = value;
    }
    if (!hasResolvedValue(pc[key])) {
      pc[key] = value;
    }
  }

  if (!pc.country && data.country) {
    pc.country = data.country;
  }

  const title = (
    doc.name
    || `${pc.first_name || ''} ${pc.last_name || ''}`.trim()
    || 'Persona'
  );
  const age = pc.age;
  const occupation = pc.occupation || pc.job_title;

  const tags = [];
  if (typeof t === 'function') {
    if (age !== undefined && age !== null && age !== '') {
      tags.push(t('personagrid.ageTag', { age }));
    }
    if (occupation) {
      tags.push(t('personagrid.jobTag', { occupation }));
    }
  }

  const description = typeof data.description === 'string'
    ? data.description
    : '';

  // Promote narrative / section fields so PersonaInfo panels can read them
  // at the top level (same shape as the old static personas*.js catalogs).
  const sectionFields = {};
  for (const [key, value] of Object.entries(data)) {
    if (RESERVED_DATA_KEYS.has(key)) continue;
    if (key === 'country' || key === 'image' || key === 'image_slug' || key === 'icons') continue;
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) || (typeof value === 'object')) {
      sectionFields[key] = value;
    }
  }

  return {
    persona_id: doc.persona_id,
    id: doc.persona_id,
    name: title,
    title,
    domain: doc.domain,
    description,
    country: data.country || pc.country || '',
    personal_characteristics: pc,
    personalCharacteristics: pc,
    personal_interests: data.personal_interests || data.personalInterests || [],
    devices: Array.isArray(data.devices) ? data.devices : [],
    personality: Array.isArray(data.personality) ? data.personality : [],
    big_five_profile: data.big_five_profile || data.bigFiveProfile || {},
    bigFiveProfile: data.big_five_profile || data.bigFiveProfile || {},
    image: data.image || undefined,
    image_slug: data.image_slug,
    tags,
    icons: Array.isArray(data.icons) ? data.icons : [],
    created: formatDate(doc.created_at) || String(new Date().getFullYear()),
    created_at: doc.created_at || null,
    updated_at: doc.updated_at || null,
    data,
    tags_raw: doc.tags || [],
    is_ai_generated: Array.isArray(doc.tags) && doc.tags.includes('ai_generated'),
    ...sectionFields,
  };
}

async function enrichImagesFromStaticCatalog(domain, personas) {
  const missing = personas.filter((p) => !p.image);
  if (!missing.length) return personas;

  const staticList = await loadStaticPersonasForLookup(domain);
  if (!staticList.length) return personas;

  const byTitle = new Map(
    staticList.map((p) => [(p.title || p.name || '').trim().toLowerCase(), p]),
  );

  return personas.map((persona) => {
    if (persona.image) return persona;
    const match = byTitle.get((persona.title || persona.name || '').trim().toLowerCase());
    if (!match) return persona;
    const slug = match.name || match.id;
    const image = match.image
      || (slug ? `personas_pics/${slug}.jpg` : undefined);
    if (!image) return persona;
    return {
      ...persona,
      image,
      image_slug: typeof slug === 'string' ? slug : persona.image_slug,
      icons: persona.icons?.length ? persona.icons : (match.icons || []),
    };
  });
}

/**
 * Primary B5 loader: active manual (non-ai_generated) personas for a domain.
 */
function isAiGeneratedPersonaDoc(doc) {
  return Array.isArray(doc?.tags) && doc.tags.includes('ai_generated');
}

export async function loadDomainPersonasFromApi(domain, t = null) {
  if (!domain) return [];
  try {
    const response = await fetch_personas_by_domain(domain);
    const docs = (response?.data || []).filter((doc) => !isAiGeneratedPersonaDoc(doc));
    const mapped = docs.map((doc) => mapApiPersonaToUi(doc, { t })).filter(Boolean);
    return enrichImagesFromStaticCatalog(domain, mapped);
  } catch (error) {
    console.error('Failed to load personas from API:', error);
    return [];
  }
}

/**
 * Backward-compatible name used by Grid / Chat / Interview.
 * Now API-first (B5) — no static-first overlay.
 */
export async function loadDomainPersonasWithOverlay(domain, t = null) {
  return loadDomainPersonasFromApi(domain, t);
}

/**
 * @deprecated Prefer loadDomainPersonasFromApi. Kept as alias for rare callers.
 */
export async function loadDomainPersonas(domain, t = null) {
  return loadDomainPersonasFromApi(domain, t);
}

export async function fetchBackendPersonaDocs(domain) {
  try {
    const response = await fetch_saved_personas_by_domain(domain);
    const docs = response?.data || [];
    const byName = new Map(docs.map((doc) => [(doc.name || '').trim().toLowerCase(), doc]));
    return { byName, docs, ok: true };
  } catch (error) {
    console.error('Failed to load backend personas:', error);
    return { byName: new Map(), docs: [], ok: false };
  }
}

export async function loadSavedPersonasForDomain(domain) {
  // AI-generated personas only (tagged ai_generated). Catalog/manual personas
  // stay on the default Personas list via loadDomainPersonasFromApi.
  try {
    const response = await fetch_generated_personas(domain);
    const docs = (response?.data || []).filter(isAiGeneratedPersonaDoc);
    return normalizeGeneratedPersonaRecords(docs);
  } catch (error) {
    console.error('Failed to load generated personas:', error);
    return [];
  }
}

export async function fetchPersonaById(domain, personaId) {
  if (!domain || !personaId) return null;
  try {
    const response = await fetch_saved_personas_by_domain(domain);
    const docs = response?.data || [];
    const doc = docs.find((item) => String(item.persona_id) === String(personaId));
    return doc ? mapApiPersonaToUi(doc) : null;
  } catch (error) {
    console.error('Failed to fetch persona by id:', error);
    return null;
  }
}

export function normalizeGeneratedPersonaRecords(personas = []) {
  return personas.map((persona) => ({
    ...persona,
    data: Array.isArray(persona.data)
      ? persona.data
      : Object.entries(persona.data || {}).map(([key, value]) => ({ key, value })),
  }));
}

/** Map a UI/API persona document into the generated-persona card/detail shape. */
export function toGeneratedPersonaView(persona) {
  if (!persona) return null;
  const raw = {
    persona_id: persona.persona_id || persona.id,
    name: persona.name || persona.title,
    domain: persona.domain,
    data: persona.data,
    tags: persona.tags || persona.tags_raw || ['ai_generated'],
    created_at: persona.created_at || null,
  };
  return normalizeGeneratedPersonaRecords([raw])[0];
}

// Legacy exports kept so older Management helpers that imported overlay
// helpers do not crash if still referenced during transition.
export function findBackendMatch() {
  return undefined;
}

export function overlayBackendData() {
  return null;
}

export function normalizePersonaRecord(persona) {
  return persona;
}
